import type { Booking } from 'types/booking';

/*
 * Reefer handling is a catalog capability: the size-type catalog's `reefer` flag and its GENSET
 * class decide, never the spelling of a code. The code regexes that used to live here missed codes
 * admins had added (45R1, and 40RH with 602 units in production), so a legitimate reefer could be
 * selected and the panel silently not appear. The flag was seeded from those regexes (REST changelog
 * #86), so day one behaves exactly as before; admins own it from then on.
 */
export interface ReeferCatalog {
  isReefer: (code?: string | null) => boolean;
  isGenset: (code?: string | null) => boolean;
}

/* Whether any of these size types needs reefer or genset handling, by the catalog. */
export const isReeferOrGenset = (sizeTypes: string[] | null | undefined, catalog: ReeferCatalog): boolean =>
  (sizeTypes ?? []).some(st => catalog.isReefer(st) || catalog.isGenset(st));

/*
 * Whether the REEFER DETAILS panel should be offered for this move.
 *
 * Offered whenever anything in the move indicates reefer handling — the container's catalog flag, a
 * genset present, a booking carrying reefer instructions, or values already recorded on the unit.
 * Showing the panel when it turns out not to be needed costs an unused field; hiding it when it was
 * needed loses the reading entirely.
 */
export const shouldOfferReeferDetails = (
  input: {
    containerSizeType?: string | null;
    gensetSizeType?: string | null;
    gensetUnitNumber?: string | null;
    bookingRequiresGenset?: boolean | null;
    bookingVentilation?: string | null;
    recordedTemperature?: number | null;
    recordedVentilation?: string | null;
  },
  catalog: ReeferCatalog
): boolean =>
  catalog.isReefer(input.containerSizeType) ||
  catalog.isGenset(input.gensetSizeType) ||
  !!input.gensetUnitNumber?.length ||
  !!input.bookingRequiresGenset ||
  !!input.bookingVentilation?.length ||
  (input.recordedTemperature !== null && input.recordedTemperature !== undefined) ||
  !!input.recordedVentilation?.length;

/* Booking reefer instructions at the gate */

export type ReeferField = 'temperature' | 'humidity' | 'ventilation';

export const REEFER_FIELDS: readonly ReeferField[] = ['temperature', 'humidity', 'ventilation'];

/* The container's recorded reefer values, as the gate form holds them. */
export type ReeferReadings = {
  temperature?: number | null;
  humidity?: number | null;
  ventilation?: string | null;
};

/* The booking's instructions in the form the gate records them; a field is absent when unusable. */
export type BookingReeferValues = {
  temperature?: number;
  humidity?: number;
  ventilation?: string;
};

/*
 * A unit keeps the decimals it is given, so what an inspector types is recorded exactly as typed. A value
 * taken from a booking is different: an EDI booking converted from °F is not rounded (0 °F is
 * -17.777779), so it is recorded to the one decimal the `Booking: …` line shows. Nearest tenth, halves
 * away from zero so both signs behave the same.
 */
export const toOneDecimal = (value: number): number => {
  const rounded = (Math.sign(value) * Math.round(Math.abs(value) * 10)) / 10;
  return rounded === 0 ? 0 : rounded; /* no negative zero */
};

/* A ventilation is a number, optionally followed by `%`. EDI sends 0/10/25/50/75/100; people type text. */
const VENTILATION_NUMBER = /^\s*(\d+(?:\.\d+)?)\s*%?\s*$/;

const ventilationNumber = (ventilation?: string | null): string | undefined =>
  ventilation?.match(VENTILATION_NUMBER)?.[1];

/*
 * What of a booking's instructions can be put in the gate fields. A missing value is unspecified, never
 * zero. A ventilation that is not a number (`CLOSED`) is an instruction to read, not a value to fill.
 */
export const usableBookingReefer = (booking?: Partial<Booking> | null): BookingReeferValues => {
  const usable: BookingReeferValues = {};
  if (typeof booking?.bookingTemperature === 'number') usable.temperature = toOneDecimal(booking.bookingTemperature);
  if (typeof booking?.humidity === 'number') usable.humidity = toOneDecimal(booking.humidity);
  const ventilation = ventilationNumber(booking?.ventilation);
  if (ventilation !== undefined) usable.ventilation = ventilation;
  return usable;
};

const isEmptyReading = (value: unknown): boolean => value === null || value === undefined || value === '';

/* Whether a recorded value already says what the booking says, compared as recorded. */
const matchesSuggestion = (field: ReeferField, value: ReeferReadings[ReeferField], suggestion: number | string) => {
  if (field === 'ventilation') {
    const recorded = ventilationNumber(value as string | null | undefined);
    return recorded !== undefined && Number(recorded) === Number(suggestion);
  }
  return typeof value === 'number' && value === suggestion;
};

export type ReeferInstructionField = {
  /* the booking's instruction as the inspector reads it, e.g. `-17.8 °C` */
  text: string;
  /* the value Apply would record, when there is one */
  suggestion?: number | string;
  /* whether Apply is offered: a reefer, a usable value, and a recorded value that says otherwise */
  canApply: boolean;
};

export type BookingReeferView = {
  genset: 'REQUIRED' | 'NOT REQUIRED' | 'NOT SPECIFIED';
  fields: Record<ReeferField, ReeferInstructionField>;
};

/*
 * The booking's instructions for the REEFER DETAILS panel, or null when no booking is attached. Only a
 * booking the gate has resolved (it has an id) carries instructions; a number still being typed does not.
 */
export const bookingReeferView = (
  booking: Partial<Booking> | null | undefined,
  readings: ReeferReadings,
  isReefer: boolean
): BookingReeferView | null => {
  if (typeof booking?.id !== 'number') return null;
  const usable = usableBookingReefer(booking);
  const ventilationText = booking.ventilation?.trim();
  const texts: Record<ReeferField, string> = {
    temperature:
      typeof booking.bookingTemperature === 'number' ? `${toOneDecimal(booking.bookingTemperature)} °C` : 'not specified',
    humidity: typeof booking.humidity === 'number' ? `${toOneDecimal(booking.humidity)} %` : 'not specified',
    ventilation: !ventilationText
      ? 'not specified'
      : usable.ventilation !== undefined
      ? `${usable.ventilation} %`
      : ventilationText
  };
  const field = (name: ReeferField): ReeferInstructionField => {
    const suggestion = usable[name];
    return {
      text: texts[name],
      suggestion,
      canApply: isReefer && suggestion !== undefined && !matchesSuggestion(name, readings[name], suggestion)
    };
  };
  return {
    genset:
      booking.requiresGenset === true
        ? 'REQUIRED'
        : booking.requiresGenset === false
        ? 'NOT REQUIRED'
        : 'NOT SPECIFIED',
    fields: { temperature: field('temperature'), humidity: field('humidity'), ventilation: field('ventilation') }
  };
};

/*
 * What decides a fill: a different booking, the container becoming (or ceasing to be) a reefer, or a
 * different unit loaded into the column. Identities are ids, so re-validating the same booking or unit
 * — which replaces the objects with equal data — is not a change.
 */
export type ReeferAutofillKey = {
  bookingId: number | null;
  isReefer: boolean;
  unitId: number | null;
};

/* The value each field was filled with, for fields whose value came from a booking and was not edited. */
export type ReeferOwnership = Partial<Record<ReeferField, number | string>>;

export const sameReeferAutofillKey = (a: ReeferAutofillKey, b: ReeferAutofillKey): boolean =>
  a.bookingId === b.bookingId && a.isReefer === b.isReefer && a.unitId === b.unitId;

/*
 * The fill for one change of key.
 *
 * A field still holding the value a booking put there follows the booking: it takes the new booking's
 * value, or is emptied when there is none (booking removed, value missing, container no longer a reefer).
 * An empty field takes the booking's value. Anything else is the inspector's and is never touched; the
 * panel offers Apply instead. Ownership is also checked by value, because a unit lookup replaces the
 * whole container without the panel ever seeing an edit.
 */
export const planReeferAutofill = (
  prevKey: ReeferAutofillKey,
  nextKey: ReeferAutofillKey,
  readings: ReeferReadings,
  owned: ReeferOwnership,
  usable: BookingReeferValues
): { patch: ReeferReadings; owned: ReeferOwnership } => {
  if (sameReeferAutofillKey(prevKey, nextKey)) return { patch: {}, owned };

  const applicable = nextKey.isReefer && nextKey.bookingId !== null;
  const patch: Record<string, number | string | null> = {};
  const nextOwned: ReeferOwnership = {};

  REEFER_FIELDS.forEach(field => {
    const recorded = readings[field];
    const stillOwned = owned[field] !== undefined && owned[field] === recorded;
    if (!stillOwned && !isEmptyReading(recorded)) return;

    const suggestion = applicable ? usable[field] : undefined;
    if (suggestion !== undefined) {
      if (suggestion !== recorded) patch[field] = suggestion;
      nextOwned[field] = suggestion;
    } else if (stillOwned) {
      patch[field] = null;
    }
  });

  return { patch: patch as ReeferReadings, owned: nextOwned };
};
